import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  FileWarning, CheckCircle2, XCircle, AlertTriangle,
  Loader2, ArrowRightLeft, Send,
} from 'lucide-react';
import MessageThread from '../../components/MessageThread';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RfiPublicPage() {
  const { rfiId } = useParams();
  const [rfi, setRfi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [decision, setDecision] = useState('');
  const [notes, setNotes] = useState('');
  const [responderName, setResponderName] = useState('');
  const [responderEmail, setResponderEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [msgName, setMsgName] = useState('');
  const [msgEmail, setMsgEmail] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/closeout/rfis/public/${rfiId}`);
        if (res.ok) setRfi(await res.json());
        else setRfi(null);
      } catch { setRfi(null); }
      finally { setLoading(false); }
    })();
  }, [rfiId]);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/closeout/rfis/public/${rfiId}/messages`);
      if (res.ok) setMessages(await res.json());
    } catch {}
  }, [rfiId]);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  const handleSendMessage = async (text) => {
    const res = await fetch(`${API_URL}/closeout/rfis/public/${rfiId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        text,
        sender_name: msgName || responderName,
        sender_email: msgEmail || responderEmail,
      }),
    });
    if (res.ok) {
      await fetchMessages();
    } else {
      throw new Error('Failed to send message');
    }
  };

  const handleSubmit = async () => {
    if (!decision) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/closeout/rfis/public/${rfiId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, notes, responder_name: responderName, responder_email: responderEmail }),
      });
      if (res.ok) {
        setSubmitted(true);
        const updated = await fetch(`${API_URL}/closeout/rfis/public/${rfiId}`);
        if (updated.ok) setRfi(await updated.json());
      }
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!rfi) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">
      <div className="text-center">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <p className="text-lg font-medium">RFI Not Found</p>
        <p className="text-sm text-slate-400 mt-1">This RFI link may be invalid or expired.</p>
      </div>
    </div>
  );

  const hasResponse = rfi.response || rfi.status !== 'open';
  const specified = rfi.specified || {};
  const sub = rfi.submitted || {};

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileWarning className="w-5 h-5 text-amber-500" />
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider">Request for Information</p>
              <p className="font-mono text-sm text-white">{rfi.rfi_number}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-400">{rfi.project_name}</p>
            <p className="font-mono text-xs text-slate-500">{rfi.project_code}</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Subject */}
        <div>
          <p className="text-lg font-medium text-white">{rfi.subject}</p>
          {rfi.trade && <p className="text-xs text-slate-400 mt-1">Trade: {rfi.trade}</p>}
          <p className="text-xs text-slate-500 mt-0.5">Created: {new Date(rfi.created_at).toLocaleDateString()}</p>
        </div>

        {/* Description */}
        {rfi.description && (
          <div className="bg-slate-900 border border-slate-800 rounded p-4">
            <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{rfi.description}</p>
          </div>
        )}

        {/* Side by side comparison */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-900 border border-slate-800 rounded p-4">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Specified (Original Design)</p>
            <p className="text-sm font-medium text-white">{specified.make || '—'} {specified.model || ''}</p>
            <p className="text-xs text-slate-400 mt-0.5">{specified.equipment_type || '—'}</p>
            {specified.location && <p className="text-xs text-slate-500 mt-0.5">Location: {specified.location}</p>}
          </div>
          <div className="bg-amber-950/20 border border-amber-800/30 rounded p-4">
            <p className="text-[10px] text-amber-500 uppercase tracking-wider mb-2 flex items-center gap-1">
              <ArrowRightLeft className="w-3 h-3" /> Proposed Substitution
            </p>
            <p className="text-sm font-medium text-amber-400">{sub.make || '—'} {sub.model || ''}</p>
            <p className="text-xs text-slate-400 mt-0.5">{sub.equipment_type || '—'}</p>
            {sub.specifications && Object.keys(sub.specifications).length > 0 && (
              <div className="mt-2 space-y-0.5">
                {Object.entries(sub.specifications).map(([k, v]) => (
                  <p key={k} className="text-[10px] text-slate-500">
                    <span className="text-slate-400">{k}:</span> {String(v)}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Status / Response */}
        {hasResponse && rfi.response ? (
          <div className={`border rounded p-4 ${
            rfi.status === 'approved' ? 'bg-emerald-950/20 border-emerald-800/30' :
            rfi.status === 'rejected' ? 'bg-red-950/20 border-red-800/30' :
            'bg-blue-950/20 border-blue-800/30'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              {rfi.status === 'approved' ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> :
               rfi.status === 'rejected' ? <XCircle className="w-4 h-4 text-red-500" /> :
               <AlertTriangle className="w-4 h-4 text-blue-400" />}
              <p className="text-sm font-medium text-white capitalize">{rfi.status.replace('_', ' ')}</p>
            </div>
            {rfi.response.notes && <p className="text-xs text-slate-300 mt-1">{rfi.response.notes}</p>}
            <p className="text-[10px] text-slate-500 mt-2">
              By {rfi.response.responder_name || rfi.response.responder_email || 'Unknown'} on {new Date(rfi.response.responded_at).toLocaleDateString()}
            </p>
          </div>
        ) : submitted ? (
          <div className="bg-emerald-950/20 border border-emerald-800/30 rounded p-4 text-center">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            <p className="text-sm text-emerald-400">Response submitted successfully</p>
          </div>
        ) : (
          /* Response Form */
          <div className="bg-slate-900 border border-slate-800 rounded p-5 space-y-4" data-testid="rfi-response-form">
            <p className="text-xs font-heading uppercase tracking-wider text-slate-400">Your Response</p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-slate-500 uppercase">Your Name</label>
                <input value={responderName} onChange={(e) => setResponderName(e.target.value)}
                  className="w-full mt-0.5 px-3 py-2 text-xs bg-slate-800 border border-slate-700 rounded text-white focus:outline-none focus:border-blue-500"
                  placeholder="John Smith, AIA" data-testid="rfi-responder-name" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase">Email</label>
                <input value={responderEmail} onChange={(e) => setResponderEmail(e.target.value)}
                  className="w-full mt-0.5 px-3 py-2 text-xs bg-slate-800 border border-slate-700 rounded text-white focus:outline-none focus:border-blue-500"
                  placeholder="jsmith@architecture.com" data-testid="rfi-responder-email" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] text-slate-500 uppercase">Decision</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'approve_substitution', label: 'Approve Substitution', icon: CheckCircle2, color: 'border-emerald-600 text-emerald-400 bg-emerald-950/30' },
                  { value: 'reject_substitution', label: 'Reject — Use Specified', icon: XCircle, color: 'border-red-600 text-red-400 bg-red-950/30' },
                  { value: 'request_info', label: 'Need More Info', icon: AlertTriangle, color: 'border-blue-600 text-blue-400 bg-blue-950/30' },
                ].map(opt => (
                  <button key={opt.value}
                    onClick={() => setDecision(opt.value)}
                    data-testid={`rfi-decision-${opt.value}`}
                    className={`flex items-center gap-2 p-3 rounded border text-xs transition-colors ${
                      decision === opt.value ? opt.color : 'border-slate-700 text-slate-400 hover:border-slate-600'
                    }`}>
                    <opt.icon className="w-4 h-4 shrink-0" />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] text-slate-500 uppercase">Notes / Comments</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                className="w-full mt-0.5 px-3 py-2 text-xs bg-slate-800 border border-slate-700 rounded text-white focus:outline-none focus:border-blue-500 resize-none"
                placeholder="Optional notes about your decision..." data-testid="rfi-notes" />
            </div>

            <button onClick={handleSubmit} disabled={!decision || submitting}
              data-testid="rfi-submit-response"
              className="flex items-center gap-2 px-5 py-2.5 text-xs font-heading uppercase tracking-wider bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors disabled:opacity-50">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Submit Response
            </button>
          </div>
        )}
      </div>

      {/* Message Thread */}
      <div className="max-w-3xl mx-auto px-6 pb-8">
        <div className="bg-slate-900 border border-slate-800 rounded overflow-hidden">
          <MessageThread
            messages={messages}
            onSend={handleSendMessage}
            senderRole="architect"
            placeholder="Ask a question about this RFI..."
            showIdentity={true}
            name={msgName || responderName}
            email={msgEmail || responderEmail}
            onNameChange={setMsgName}
            onEmailChange={setMsgEmail}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-slate-800 px-6 py-4 mt-8">
        <p className="text-center text-[10px] text-slate-600">
          Powered by FMiQ-One — Construction Closeout Intelligence
        </p>
      </div>
    </div>
  );
}
